import * as React from "react";
import { connect } from "react-redux";
import withStyles from "@material-ui/core/styles/withStyles";
import PropTypes from "prop-types";
import WarnIcon from "../../assets/warn-icon.svg";
import debounce from "lodash.debounce";
import SectionHeader from "../../components/UI/SectionHeader";
import PasRepCodeNotification from "./components/PasRepCodeNotification";
import PasEmailRequestedModal from "./components/PasEmailRequestedModal";
import AddRepCodeModal from "./components/AddRepCodeModal";
import ConfirmDelete from "../../components/ConfirmDelete";
import { Button } from "@material-ui/core";
import {
  appAction,
  householdAction,
  portfolioAction,
  repCodeAction,
  userAction,
} from "../../actions";
import Fade from "@material-ui/core/Fade";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import ApiMessage from "../../components/UI/ApiMessage";
import AddSplitRepCodeModal from "./components/AddSplitRepCodeModal";

const styles = (theme) => ({
  brokerDealerTitle: {
    fontSize: "20px",
    lineHeight: "22px",
    color: theme.palette.grey[600],
  },
  repCodeBody: {
    fontSize: "18.25px",
    lineHeight: "22px",
    weight: 400,
    color: theme.palette.grey[600],
  },
  contentWrapper: {
    width: "100%",
    display: `flex`,
    flexDirection: "column",
    [theme.breakpoints.down("md")]: {
      paddingLeft: "5%",
    },
  },
  formWrapper: {
    maxWidth: "100%",
    padding: "0 0 80px 0",
    position: "relative",
    [theme.breakpoints.down("md")]: {
      padding: "18px 0",
    },
    "&.disabled": {
      display: "none",
    },
  },
  row: {
    display: "block",
    flexWrap: "wrap",
  },
  label: {
    fontSize: 20,
    "& span": {
      color: theme.palette.primary.main,
    },
    [theme.breakpoints.down("md")]: {
      fontSize: 16,
      width: 120,
    },
  },
  input: {
    minWidth: 350,
    marginRight: 20,
    "& .MuiInputBase-root": {
      color: theme.palette.primary.main,
    },
  },
  selectMenu: {
    fontSize: 20,
    [theme.breakpoints.down("md")]: {
      fontSize: 16,
    },
    minHeight: 21,
  },
  selectDisabled: {
    color: theme.palette.grey[600],
  },
  saveUserProfileBtn: {
    position: "absolute",
    bottom: 40,
    right: 40,
    [theme.breakpoints.down("md")]: {
      position: "static",
      float: "right",
      fontSize: 12,
    },
  },
  table: {
    maxWidth: 600,
    marginBottom: 40,
  },
});

class UserRepCodes extends React.Component {
  constructor(props) {
    super(props);
    const { user } = this.props.user;

    const initialState = {
      bdAffiliation: user.bdAffiliation || "",
      bdRepCode: user.bdRepCode || "",
      formDirty: false,
      advisors: [""],
    };

    this.state = initialState;

    this.debouncedFetch = debounce(this.loadBrokerDealers, 400);

    this.confirmDeleteRef = React.createRef();
  }

  loadBrokerDealers = (inputValue, callback) => {
    this.props
      .dispatch(userAction.getBrokerDealers(inputValue))
      .then((res) => {
        const cleanedData = res.data.reduce(
          (acc, currentValue) => [
            ...acc,
            { label: currentValue.name, value: currentValue.name },
          ],
          []
        );
        callback(cleanedData);
      })
      .catch((error) => callback(error, null));
  };

  handleInput = (name) => (event) => {
    const { value } = event.target;

    const newState = {};
    newState[name] = value;
    this.setState({ ...newState, formDirty: true });
  };

  saveUserRepCode = (data) => {
    const { bdRepCode, custodialRepCode, advisors, isSplit, repCodeId } = data;
    const userBdRepCode = {
      brokerDealerId: this.props.user.user.brokerDealer.id,
      bdRepCode: bdRepCode.toString(),
      custodialRepCode,
      advisors,
      repCodeId,
      isSplit,
    };

    this.props.dispatch(repCodeAction.saveUserBdRepCode(userBdRepCode));
  };
  handleModalClose = () => {
    const data = { shown: true };
    this.props.dispatch(repCodeAction.updateInProcessShown(data));
  };
  requestPasRepCode = (repcode) => {
    const data = {
      repcode,
    };
    this.props.dispatch(repCodeAction.requestPasRepCode(data));
  };

  deleteSplitRepCode = (data) => {
    const {
      bdRepCode,
      custodialRepCode,
      UserId,
      advisors,
      isSplit,
      id,
      BrokerDealerId,
    } = data;
    const userBdRepCode = {
      brokerDealerId: BrokerDealerId,
      UserId,
      bdRepCodeId: id,
      bdRepCode: bdRepCode.toString(),
      custodialRepCode: custodialRepCode,
      advisors,
      isSplit,
    };
    this.props.dispatch(repCodeAction.deleteSplitRepCode(userBdRepCode));
  };

  componentDidMount() {
    const { dispatch } = this.props;

    dispatch(appAction.setUsersNav(true));
    dispatch(appAction.toggleMainNav(false));
    dispatch(householdAction.clearCurrentHousehold());
    dispatch(portfolioAction.clearCurrentPortfolio());

    window.scrollTo(0, 0);
  }

  componentWillUnmount() {
    this.props.dispatch(userAction.clearApiMessage());
  }

  render() {
    const { classes, user, app, theme } = this.props;

    const repCodes = user.user.repCodes
      ? user.user.repCodes.filter((repcode) => !repcode.isSplit)
      : [];
    const splitRepCodes = user.user.repCodes
      ? user.user.repCodes.filter((repcode) => repcode.isSplit)
      : [];

    return (
      <Fade in>
        <div className={classes.contentWrapper}>
          <div className={classes.content}>
            <SectionHeader text={"Rep Codes"} />
            {user.message && <ApiMessage message={user.message} />}
            <Grid container spacing={2} alignItems={"center"}>
              <Grid item xs={12}>
                <Typography variant="caption" style={{ color: "#000000" }}>
                  BD Affiliation
                </Typography>
                <Typography className={classes.brokerDealerTitle} gutterBottom>
                  {user.user.bdAffiliation || "--"}
                </Typography>
              </Grid>

              <Grid item xs={12} container spacing={3}>
                <Grid item xs={12} container>
                  <Grid
                    item
                    xs={12}
                    sm={3}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Typography className={classes.label}>
                      My Rep Codes
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <AddRepCodeModal
                      userCrd={user.user.advisorCrdRef}
                      saveUserRepCode={this.saveUserRepCode}
                      dialogAnchorComponent={
                        <Button
                          variant={"outlined"}
                          size={"small"}
                          color={"primary"}
                          style={{ marginRight: 10 }}
                          data-cy="repCodes.add"
                        >
                          Add
                        </Button>
                      }
                    />
                  </Grid>
                </Grid>

                {repCodes &&
                  repCodes.length > 0 &&
                  repCodes.map((repCode, index) => (
                    <Grid item xs={12} container key={repCode.id}>
                      {index === 0 && (
                        <PasEmailRequestedModal
                          show={user.user.inProcessShown}
                          handleModalClose={this.handleModalClose}
                        />
                      )}
                      <Grid item xs={12} sm={3} md={2}>
                        <Typography
                          variant="caption"
                          style={{ color: "#000000" }}
                        >
                          BD Rep Code
                        </Typography>
                        <Typography
                          className={classes.repCodeBody}
                          gutterBottom
                          data-cy="repCodes.item.bdRepCode"
                        >
                          {repCode.bdRepCode}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={3} md={2}>
                        <Typography
                          variant="caption"
                          style={{ color: "#000000" }}
                        >
                          CRD Number
                        </Typography>
                        <Typography
                          className={classes.repCodeBody}
                          gutterBottom
                        >
                          {user.user.advisorCrdRef}
                        </Typography>
                      </Grid>

                      {
                        app.allowRequestCustodialRepCode && (
                          <Grid item xs={12} sm={3} md={2}>
                            <Typography
                              variant="caption"
                              style={{ color: "#000000" }}
                            >
                              PAS Rep Code
                            </Typography>
                            <Grid item xs={12}>
                              {repCode.custodialRepCode ? (
                                <Typography
                                  className={classes.repCodeBody}
                                  gutterBottom
                                >
                                  {repCode.custodialRepCode}
                                </Typography>
                              ) : repCode.inProcess ? (
                                <React.Fragment>
                                  <Typography
                                    style={{ display: "inline" }}
                                    className={classes.repCodeBody}
                                    gutterBottom
                                  >
                                    In Progress
                                  </Typography>
                                  <img
                                    src={WarnIcon}
                                    alt="warn link icon"
                                    style={{
                                      color: theme.palette.secondary.main,
                                      display: "inline",
                                      alignSelf: "center",
                                      position: "relative",
                                      marginLeft: "4px",
                                    }}
                                  />
                                </React.Fragment>
                              ) : (
                                <PasRepCodeNotification
                                  requestPasRepCode={this.requestPasRepCode}
                                  repCode={repCode}
                                  dialogAnchorComponent={
                                    <Button
                                      variant={"outlined"}
                                      size={"small"}
                                      color={"primary"}
                                    >
                                      Request
                                    </Button>
                                  }
                                />
                              )}
                            </Grid>
                          </Grid>
                        )
                      }

                      <Grid
                        item
                        xs={12}
                        container
                        style={{ alignItems: "center" }}
                      >
                        {!repCode.hasHousehold && (
                          <AddRepCodeModal
                            userCrd={user.user.advisorCrdRef}
                            bdRepCode={repCode.bdRepCode}
                            repCode={repCode}
                            saveUserRepCode={this.saveUserRepCode}
                            dialogAnchorComponent={
                              <Button
                                variant={"outlined"}
                                size={"small"}
                                color={"primary"}
                                style={{ marginRight: 10 }}
                              >
                                Edit
                              </Button>
                            }
                          />
                        )}
                      </Grid>
                    </Grid>
                  ))}
              </Grid>
            </Grid>
            <Divider style={{ margin: "20px 0", width: "84%" }} />

            <Grid container spacing={2}>
              <Grid item xs={12} container>
                <Grid
                  item
                  xs={12}
                  sm={3}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Typography className={classes.label}>
                    Split Rep Codes
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <AddSplitRepCodeModal
                    user={user.user}
                    handleSaveSplitRepCode={this.saveUserRepCode}
                    dialogAnchorComponent={
                      <Button
                        variant={"outlined"}
                        size={"small"}
                        color={"primary"}
                      >
                        Add Split
                      </Button>
                    }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={3}>
                {splitRepCodes.length !== 0 ? (
                  <React.Fragment>
                    {splitRepCodes &&
                      splitRepCodes.map((splitRepCode, index) => (
                        <Grid
                          item
                          xs={12}
                          container
                          key={splitRepCode.id}
                          spacing={2}
                          style={{
                            marginBottom:
                              splitRepCodes.length - 1 === index
                                ? undefined
                                : 24,
                          }}
                        >
                          <Grid item xs={12} container>
                            <Grid item xs={12} sm={3} md={4}>
                              <Typography
                                variant="caption"
                                style={{ color: "#000000" }}
                              >
                                Split BD Rep Code
                              </Typography>
                              <Typography
                                className={classes.repCodeBody}
                                gutterBottom
                              >
                                {splitRepCode.bdRepCode}
                              </Typography>
                            </Grid>
                            {
                              app.allowRequestCustodialRepCode && (
                                <Grid item xs={12} sm={3} md={4}>
                                  <Grid item xs={12} container>
                                    <Grid item xs={12}>
                                      <Typography
                                        variant="caption"
                                        style={{ color: "#000000" }}
                                      >
                                        Split PAS Rep Code
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                      {splitRepCode.custodialRepCode ? (
                                        <Typography
                                          className={classes.repCodeBody}
                                          gutterBottom
                                        >
                                          {splitRepCode.custodialRepCode}
                                        </Typography>
                                      ) : splitRepCode.inProcess ? (
                                        <React.Fragment>
                                          <Typography
                                            style={{ display: "inline" }}
                                            className={classes.repCodeBody}
                                            gutterBottom
                                          >
                                            In Progress
                                          </Typography>
                                          <img
                                            src={WarnIcon}
                                            alt="warn link icon"
                                            style={{
                                              color: theme.palette.secondary.main,
                                              display: "inline",
                                              alignSelf: "center",
                                              position: "relative",
                                              marginLeft: "4px",
                                            }}
                                          />
                                        </React.Fragment>
                                      ) : (
                                        <PasRepCodeNotification
                                          requestPasRepCode={this.requestPasRepCode}
                                          repCode={splitRepCode}
                                          dialogAnchorComponent={
                                            <Button
                                              variant={"outlined"}
                                              size={"small"}
                                              color={"primary"}
                                            >
                                              Request
                                            </Button>
                                          }
                                        />
                                      )}
                                    </Grid>
                                  </Grid>
                                </Grid>
                              )
                            }
                          </Grid>
                          <Grid item xs={12} container>
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                style={{ color: "#000000" }}
                              >
                                Advisors
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Chip
                                key={user.user.id}
                                label={
                                  <Typography
                                    display="inline"
                                    variant="subtitle2"
                                  >
                                    {user.user.firstName} {user.user.lastName}
                                  </Typography>
                                }
                                style={{ margin: 6 }}
                              />
                              {splitRepCode.associatedSplitRepCodes &&
                                splitRepCode.associatedSplitRepCodes.map(
                                  (associatedSplitRepCodes) => (
                                    <Chip
                                      key={associatedSplitRepCodes.id}
                                      label={
                                        <React.Fragment>
                                          <Typography
                                            variant="subtitle2"
                                            display="inline"
                                            style={{
                                              color:
                                                associatedSplitRepCodes.isValid &&
                                                splitRepCode.hasHousehold
                                                  ? undefined
                                                  : user.user.advisorCrdRef ===
                                                    associatedSplitRepCodes.user
                                                      .advisorCrdRef
                                                  ? undefined
                                                  : theme.palette.primary.main,
                                            }}
                                          >
                                            {
                                              associatedSplitRepCodes.user
                                                .first_name
                                            }{" "}
                                            {
                                              associatedSplitRepCodes.user
                                                .last_name
                                            }
                                          </Typography>{" "}
                                          {user.user.advisorCrdRef !==
                                            associatedSplitRepCodes.user
                                              .advisorCrdRef && (
                                            <Typography
                                              display="inline"
                                              variant="body1"
                                              style={{
                                                color:
                                                  associatedSplitRepCodes.isValid &&
                                                  splitRepCode.hasHousehold
                                                    ? undefined
                                                    : user.user
                                                        .advisorCrdRef ===
                                                      associatedSplitRepCodes
                                                        .user.advisorCrdRef
                                                    ? undefined
                                                    : theme.palette.primary
                                                        .main,
                                              }}
                                            >
                                              (
                                              {
                                                associatedSplitRepCodes.user
                                                  .advisorCrdRef
                                              }
                                              )
                                            </Typography>
                                          )}
                                        </React.Fragment>
                                      }
                                      style={{ margin: 6 }}
                                    />
                                  )
                                )}
                            </Grid>
                          </Grid>

                          {!splitRepCode.hasHousehold &&
                            (splitRepCode.id === splitRepCode.splitBy ||
                              splitRepCode.splitBy === null) && (
                              <Grid
                                item
                                xs={6}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {/* open modal with data */}

                                <AddSplitRepCodeModal
                                  user={user.user}
                                  editSplitRepCodeData={splitRepCode}
                                  handleSaveSplitRepCode={this.saveUserRepCode}
                                  dialogAnchorComponent={
                                    <Button
                                      variant={"outlined"}
                                      size={"small"}
                                      color={"primary"}
                                    >
                                      Edit
                                    </Button>
                                  }
                                />

                                <ConfirmDelete
                                  dialogTitle="Confirm"
                                  dialogText="Are you sure you want to delete this split rep code?"
                                  dialogConfirmedCallback={() =>
                                    this.deleteSplitRepCode(splitRepCode)
                                  }
                                  ref={this.confirmDeleteRef}
                                >
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => this.confirmDeleteRef.current.handleModalOpen()}
                                    style={{marginLeft: 16}}
                                  >
                                    Delete
                                  </Button>
                                </ConfirmDelete>
                              </Grid>
                            )}
                        </Grid>
                      ))}
                  </React.Fragment>
                ) : (
                  <Grid item xs={12} container>
                    <Typography variant="body1">
                      You have no split rep codes.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Divider style={{ margin: "20px 0", width: "84%" }} />
          </div>
        </div>
      </Fade>
    );
  }
}

UserRepCodes.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  return {
    app: state.app,
    user: state.user,
  };
};

export default withStyles(styles, { withTheme: true })(
  connect(mapStateToProps)(UserRepCodes)
);



// WEBPACK FOOTER //
// ./src/containers/UserRepCodes/index.jsx